import React, { Component } from "react";
import { connect } from "react-redux";
import { NORMAL_LIFECYCLE_STATUS } from "../../../utilities/lifecycleStatus";
import { LabeledTextInput } from "../../TextInput";
import { LabeledDropDownSelect } from "../../DropDownSelect";
import { toggleNewDocumentProfilingExpanded } from "../../../actions/browserLocalstorageActions";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { ActionIcon } from "@mantine/core";
import "./MandatoryProfilingDialog.css";

// submit function that extracts the relevant state from the EditDialog instance
function createNewDocumentSubmitHandler({
	extension,
	createNewDocumentFromTemplate,
	directoryID,
	cognitoSub,
}) {
	return (state) => {
		const filename = `${state.value}.${extension}`;

		// Extract text values only from newDocumentDialog (or null if not present)
		const documentType = state.newDocumentDialog?.documentType
			? state.newDocumentDialog.documentType.value ||
				state.newDocumentDialog.documentType
			: null;

		const documentStatus = state.newDocumentDialog?.documentStatus
			? state.newDocumentDialog.documentStatus.value ||
				state.newDocumentDialog.documentStatus
			: null;

		const tags =
			Array.isArray(state.newDocumentDialog?.documentTags) &&
			state.newDocumentDialog.documentTags.length > 0
				? state.newDocumentDialog.documentTags.map(
						(tag) => tag.value || tag
					)
				: [];

		createNewDocumentFromTemplate(
			directoryID,
			filename,
			extension,
			cognitoSub,
			documentType,
			documentStatus,
			tags
		);
	};
}

// used by EditDialog
function createNewDocumentValidationHandler({
	extension,
	directoryID,
	documentList,
	requiredFields = [],
}) {
	return (state) => {
		const matchesFilename = documentList.filter(
			(item) =>
				item.type === "File" &&
				item.filename.toUpperCase() ===
					`${state.value}.${extension}`.toUpperCase() &&
				item.id !== directoryID
		).length;
		const deletedFileMatch = documentList.filter(
			(item) =>
				item.type === "File" &&
				item.filename.toUpperCase() ===
					`${state.value}.${extension}`.toUpperCase() &&
				item.id !== directoryID &&
				item.documentLifecycleStatus !== NORMAL_LIFECYCLE_STATUS
		).length;

		const isEmpty = state.value.trim() === "";
		const hasIllegalCharacters = state.value.match(/[<>:"/\\|?*%]/);

		// Check mandatory profiling fields (if any)
		let profilingValid = true;
		if (requiredFields.includes("documentType")) {
			const typeValue =
				state.newDocumentDialog?.documentType?.value ||
				state.newDocumentDialog?.documentType;
			profilingValid =
				profilingValid &&
				typeof typeValue === "string" &&
				typeValue.trim() !== "";
		}
		if (requiredFields.includes("documentStatus")) {
			const statusValue =
				state.newDocumentDialog?.documentStatus?.value ||
				state.newDocumentDialog?.documentStatus;
			profilingValid =
				profilingValid &&
				typeof statusValue === "string" &&
				statusValue.trim() !== "";
		}
		if (requiredFields.includes("documentTags")) {
			profilingValid =
				profilingValid &&
				state.newDocumentDialog?.documentTags &&
				Array.isArray(state.newDocumentDialog.documentTags) &&
				state.newDocumentDialog.documentTags.length > 0;
		}

		return {
			canSubmit:
				!isEmpty &&
				!matchesFilename &&
				!hasIllegalCharacters &&
				profilingValid,
			errorMessage: matchesFilename
				? "There is already a " +
					(deletedFileMatch ? "deleted" : "") +
					" file in the current directory with this name"
				: hasIllegalCharacters
					? 'New File names cannot contain any of the following characters: < > : " / \\ | ? * %'
					: isEmpty
						? "Please enter a new file name"
						: !profilingValid
							? "Please fill in all required metadata fields"
							: "",
		};
	};
}
class NewDocumentWithProfilingDialog extends Component {
	constructor(props) {
		super(props);

		const { availableOptions = {} } = props;

		const transformToDropdownFormat = (items) => {
			if (!items || !Array.isArray(items)) return [];

			return items.map((item) => {
				// If already an object with value/label, return as is (preserving id if present)
				if (typeof item === "object" && item.value !== undefined) {
					return item;
				}
				// If object with id/name (from backend), transform to {value, label, id}
				if (typeof item === "object" && item.name !== undefined) {
					return {
						value: item.name,
						label: item.name,
						id: item.id,
					};
				}
				// If string, transform to {value, label}
				return { value: item, label: item };
			});
		};

		this.state = {
			filename: props.defaultFilename || "",
			documentType: null,
			documentStatus: null,
			documentTags: [],
			documentTypeOptions: transformToDropdownFormat(
				availableOptions.types
			),
			documentStatusOptions: transformToDropdownFormat(
				availableOptions.statuses
			),
			documentTagOptions: transformToDropdownFormat(
				availableOptions.tags
			),
		};

		this.formRef = React.createRef();
		this.handleFieldChange = this.handleFieldChange.bind(this);
	}

	componentDidMount() {
		// Focus on filename input
		if (this.formRef.current && this.formRef.current.textInput) {
			setTimeout(() => {
				this.formRef.current.textInput.focus();
			}, 5);
		}

		// Trigger initial validation
		if (this.props.onValidationChange) {
			this.props.onValidationChange(this.isValid());
		}

		// Send initial data to parent
		if (this.props.onChange) {
			this.props.onChange(this.state);
		}
	}

	handleFieldChange(fieldName, value) {
		this.setState(
			{
				[fieldName]: value,
			},
			() => {
				// Call parent's onChange if provided
				if (this.props.onChange) {
					this.props.onChange(this.state);
				}

				// Call validation callback if provided
				if (this.props.onValidationChange) {
					this.props.onValidationChange(this.isValid());
				}
			}
		);
	}

	isValid() {
		const { requiredFields = [] } = this.props;

		// Check each required field
		let valid = true;

		// Filename is always required
		if (!this.state.filename || this.state.filename.trim() === "") {
			valid = false;
		}

		// Check profiling fields
		if (requiredFields.includes("documentType")) {
			const typeValue = this.state.documentType;
			valid =
				valid &&
				typeValue &&
				(typeof typeValue === "string" ||
					(typeValue.value && typeValue.value.trim()));
		}

		if (requiredFields.includes("documentStatus")) {
			const statusValue = this.state.documentStatus;
			valid =
				valid &&
				statusValue &&
				(typeof statusValue === "string" ||
					(statusValue.value && statusValue.value.trim()));
		}

		if (requiredFields.includes("documentTags")) {
			valid =
				valid &&
				Array.isArray(this.state.documentTags) &&
				this.state.documentTags.length > 0;
		}

		return valid;
	}

	handleCreateOption = (fieldName, inputValue) => {
		const newOption = {
			id: Date.now(),
			label: inputValue,
			value: inputValue,
		};

		this.setState((prevState) => {
			let optionsKey;
			if (fieldName === "documentType") {
				optionsKey = "documentTypeOptions";
			} else if (fieldName === "documentStatus") {
				optionsKey = "documentStatusOptions";
			} else if (fieldName === "documentTags") {
				optionsKey = "documentTagOptions";
			}

			return {
				[optionsKey]: [...prevState[optionsKey], newOption],
			};
		});

		return newOption;
	};

	render() {
		const {
			requiredFields = [],
			stronglySuggestProfiling = false,
			newDocumentProfilingExpanded,
			toggleNewDocumentProfilingExpanded,
		} = this.props;

		// Determine if fields are required
		const isDocumentTypeRequired = requiredFields.includes("documentType");
		const isDocumentStatusRequired =
			requiredFields.includes("documentStatus");
		const isDocumentTagsRequired = requiredFields.includes("documentTags");

		// Determine if the section can be collapsed
		const canCollapse = !stronglySuggestProfiling;

		return (
			<div>
				<div style={{ display: "flex", marginBottom: 20 }}>
					<div style={{ flexGrow: 1 }}>
						<LabeledTextInput
							label="DOCUMENT NAME"
							name="filename"
							formRef={this.formRef}
							autoFocus
							placeholder={this.props.placeholder || "Filename"}
							defaultValue={this.state.filename}
							onChange={this.handleFieldChange}
							onEnterKey={this.props.onEnterKey}
						/>
					</div>
					{this.props.extension ? (
						<div
							style={{
								display: "flex",
								marginLeft: 8,
								paddingTop: 26,
							}}
						>
							.{this.props.extension}
						</div>
					) : null}
				</div>

				{/* Conditionally show profiling fields only when enabled */}

				<div className="profiling-section">
					<div className="profiling-section-header">
						<h4 style={{ margin: 0 }}>Metadata</h4>
						{canCollapse ? (
							<span
								onClick={toggleNewDocumentProfilingExpanded}
								style={{ cursor: "pointer" }}
							>
								<ActionIcon
									variant="subtle"
									aria-label="toggle"
								>
									{newDocumentProfilingExpanded ? (
										<IconChevronUp size={32} />
									) : (
										<IconChevronDown size={32} />
									)}
								</ActionIcon>
							</span>
						) : null}
					</div>

					{newDocumentProfilingExpanded || !canCollapse ? (
						<>
							<div style={{ marginBottom: 20 }}>
								<LabeledDropDownSelect
									label={
										isDocumentTypeRequired
											? "Document Type *"
											: "Document Type"
									}
									name="documentType"
									isCreatable={
										this.props.userRights
											.CRUD_DOCUMENT_TYPES === true
									}
									isClearable
									isAsync
									options={this.state.documentTypeOptions}
									handler={this.handleFieldChange}
								/>
							</div>

							<div style={{ marginBottom: 20 }}>
								<LabeledDropDownSelect
									label={
										isDocumentStatusRequired
											? "Document Status *"
											: "Document Status"
									}
									name="documentStatus"
									isCreatable={
										this.props.userRights
											.CRUD_DOCUMENT_STATUSES === true
									}
									isClearable
									isAsync
									options={this.state.documentStatusOptions}
									handler={this.handleFieldChange}
								/>
							</div>

							<div style={{ marginBottom: 20 }}>
								<LabeledDropDownSelect
									label={
										isDocumentTagsRequired
											? "Document Tags *"
											: "Document Tags"
									}
									name="documentTags"
									isMulti
									isCreatable={
										this.props.userRights
											.CRUD_DOCUMENT_TAGS === true
									}
									isClearable
									isAsync
									options={this.state.documentTagOptions}
									handler={this.handleFieldChange}
								/>
							</div>
						</>
					) : null}
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state) => ({
	newDocumentProfilingExpanded:
		state.browserLocalstorage.newDocumentProfilingExpanded,
});

const mapDispatchToProps = {
	toggleNewDocumentProfilingExpanded,
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(NewDocumentWithProfilingDialog);

export { createNewDocumentSubmitHandler, createNewDocumentValidationHandler };
